import * as THREE from "three";

export function createWaterMaterial(time: { value: number }): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      time,
      sky: { value: new THREE.Color(0xa8dcec) },
      shallow: { value: new THREE.Color(0x3ec8d4) },
      deep: { value: new THREE.Color(0x1a7a96) },
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    fog: false,
    vertexShader: `
      attribute vec3 color;
      varying vec3 vWorld;
      varying vec3 vColor;
      varying vec3 vN;
      varying float vFog;
      void main() {
        vColor = color;
        vN = normalize(normalMatrix * normal);
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorld = wp.xyz;
        vec4 mv = viewMatrix * wp;
        vFog = -mv.z;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform vec3 sky;
      uniform vec3 shallow;
      uniform vec3 deep;
      varying vec3 vWorld;
      varying vec3 vColor;
      varying vec3 vN;
      varying float vFog;
      void main() {
        float w = sin(vWorld.x * 0.45 + time * 1.6) * cos(vWorld.z * 0.4 + time * 1.15);
        vec3 n = normalize(vN + vec3(w * 0.15, 0.0, w * 0.12));
        vec3 view = normalize(cameraPosition - vWorld);
        float fres = pow(1.0 - max(dot(n, view), 0.0), 2.4);
        vec3 col = mix(deep, shallow, 0.55 + 0.45 * w);
        col *= mix(vec3(1.0), vColor, 0.25);
        col = mix(col, sky, fres * 0.5);
        float alpha = mix(0.62, 0.88, fres);
        float fog = 1.0 - exp(-0.0075 * vFog);
        col = mix(col, sky, clamp(fog, 0.0, 0.65));
        gl_FragColor = vec4(col, alpha);
      }
    `,
  });
}

export function patchWind(material: THREE.Material, time: { value: number }): void {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.time = time;
    shader.vertexShader = `uniform float time;\n${shader.vertexShader}`;
    shader.vertexShader = shader.vertexShader.replace(
      "#include <begin_vertex>",
      `#include <begin_vertex>
       transformed.x += sin(time * 1.35 + transformed.y * 0.45 + transformed.z) * 0.07;
       transformed.z += cos(time * 1.05 + transformed.x * 0.35) * 0.05;`,
    );
  };
  material.customProgramCacheKey = () => "wind-1";
}

export function createSkyDome(): THREE.Mesh {
  const geo = new THREE.SphereGeometry(420, 32, 20);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      top: { value: new THREE.Color(0x6eb6e0) },
      mid: { value: new THREE.Color(0xc8e4f2) },
      bot: { value: new THREE.Color(0xe8d8b8) },
    },
    vertexShader: `
      varying vec3 vPos;
      void main() {
        vPos = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 top;
      uniform vec3 mid;
      uniform vec3 bot;
      varying vec3 vPos;
      void main() {
        float h = normalize(vPos).y;
        vec3 col = mix(bot, mid, smoothstep(-0.15, 0.18, h));
        col = mix(col, top, smoothstep(0.12, 0.72, h));
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  return mesh;
}
